import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Package2,
  Search,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  PackageCheck,
  PackageX,
  FileSpreadsheet,
  Eye
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatPrice } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  stock: number;
  createdAt: string;
  updatedAt: string;
}

export function ProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    price: '',
    stock: '',
  });

  // Fetch products
  const { data: products, isLoading } = useQuery({
    queryKey: ['products', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await fetchWithAuth(`/api/admin/products?${params}`);
      if (!response.ok) throw new Error('Eroare la încărcarea produselor');
      return response.json() as Promise<Product[]>;
    },
  });

  // Fetch product statistics
  const { data: stats } = useQuery({
    queryKey: ['product-stats'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/products/stats');
      if (!response.ok) throw new Error('Eroare la încărcarea statisticilor');
      return response.json() as Promise<{
        totalProducts: number;
        totalValue: number;
        lowStock: number;
        outOfStock: number;
      }>;
    },
  });

  // Create product mutation
  const createProduct = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await fetchWithAuth('/api/admin/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          price: Number(data.price),
          stock: Number(data.stock),
        }),
      });
      if (!response.ok) throw new Error('Eroare la crearea produsului');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produs creat cu succes');
      setIsCreating(false);
      setFormData({
        name: '',
        description: '',
        price: '',
        stock: '',
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Delete product mutation
  const deleteProduct = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 400) {
          throw new Error(data.message || 'Nu se poate șterge produsul deoarece există comenzi asociate');
        }
        throw new Error('Eroare la ștergerea produsului');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produs șters cu succes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createProduct.mutate(formData);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Sigur doriți să ștergeți acest produs? Această acțiune nu poate fi anulată.')) {
      deleteProduct.mutate(id);
    }
  };

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Produse</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="w-4 h-4" />
            Adaugă Produs
          </Button>
          <Button 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={async () => {
              try {
                const response = await fetchWithAuth('/api/admin/products/export', {
                  method: 'GET',
                  headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  }
                });
                if (!response.ok) {
                  throw new Error('Eroare la exportul produselor');
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `produse-${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Export error:', error);
              }
            }}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Package2 className="w-4 h-4" />
              Total Produse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.totalProducts || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Valoare Totală Stoc
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats?.totalValue || 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <PackageCheck className="w-4 h-4" />
              Stoc Redus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.lowStock || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <PackageX className="w-4 h-4" />
              Stoc Epuizat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.outOfStock || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Product Form */}
      {isCreating && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Adaugă Produs Nou
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nume</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Nume produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Descriere</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                    placeholder="Descriere produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Preț</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(f => ({ ...f, price: e.target.value }))}
                    placeholder="Preț produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Stoc</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData(f => ({ ...f, stock: e.target.value }))}
                    placeholder="Stoc disponibil"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreating(false)}
                >
                  Anulează
                </Button>
                <Button 
                  type="submit" 
                  disabled={createProduct.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {createProduct.isPending ? 'Se salvează...' : 'Salvează'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
          <Input
            className="pl-9"
            placeholder="Caută după nume..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      {/* Products Table */}
      <Card className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Nume</TableHead>
                <TableHead className="font-semibold">Preț</TableHead>
                <TableHead className="font-semibold">Stoc</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="flex items-center justify-center text-gray-500">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : products?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="text-gray-500">Nu există produse</div>
                  </TableCell>
                </TableRow>
              ) : (
                products?.map((product) => (
                  <TableRow key={product.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">#{product.id}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{product.name}</span>
                        <span className="text-sm text-gray-500 truncate max-w-md">
                          {product.description}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formatPrice(product.price)}</TableCell>
                    <TableCell>
                      <Badge className={
                        product.stock > 10
                          ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                          : product.stock > 0
                          ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                          : 'bg-red-100 text-red-800 ring-1 ring-red-200'
                      }>
                        {product.stock} bucăți
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        product.stock > 10
                          ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                          : product.stock > 0
                          ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                          : 'bg-red-100 text-red-800 ring-1 ring-red-200'
                      }>
                        {product.stock > 10
                          ? 'În Stoc'
                          : product.stock > 0
                          ? 'Stoc Limitat'
                          : 'Stoc Epuizat'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={() => navigate(`/products/${product.id}/`)}
                        >
                          <Edit className="w-4 h-4" />
                          Editează
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Șterge
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
